import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import Select, {
    components,
    ControlProps,
    DropdownIndicatorProps,
    OptionProps,
    StylesConfig
} from "react-select";

import UFOLabel from "@atlaskit/react-ufo/label";
import { token } from "@atlaskit/tokens";
import VisuallyHidden from "@atlaskit/visually-hidden";

import { checkFeatureFlagGate } from "@/utils/feature-flags";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { parseRoute } from "../../../../wac-routes";

import { LanguageGlobeIcon, languages, type LanguageType } from "./common";

/**
 * Custom React-Select's Control component to allow adding the globe icon.
 */
const Control = ({ children, ...props }: ControlProps<LanguageType>) => {
    // @ts-ignore
    const { isMouseOver } = props.selectProps;

    return (
        <components.Control {...props}>
            {/* eslint-disable-next-line local/ensure-wac-token-usage */}
            <LanguageGlobeIcon color={isMouseOver ? "#172b4d" : "#42526e"} />
            {children}
        </components.Control>
    );
};

/**
 * Custom React-Select's DropdownIndicator component to update the arrow icon.
 */
const DropdownIndicator = (props: DropdownIndicatorProps<LanguageType>) => {
    return (
        <components.DropdownIndicator {...props}>
            ▾
        </components.DropdownIndicator>
    );
};

/**
 * Maps locale values to their BCP 47 language tags where they differ.
 * e.g. WAC uses "br" for Brazilian Portuguese, but the correct lang attribute is "pt".
 */
const langOverrides: Partial<Record<string, string>> = {
    br: "pt"
};

/**
 * Custom React-Select's Option component to render lang attribute on non-English options.
 * WCAG 3.1.2: Language of Parts - Screen readers need lang attribute to pronounce non-English text correctly.
 */
const Option = ({ children, ...props }: OptionProps<LanguageType>) => (
    <components.Option {...props}>
        <span lang={langOverrides[props.data.value] ?? props.data.value}>
            {children}
        </span>
    </components.Option>
);

/**
 * Styles for the LanguageSelector container div.
 */
const languageSelectorContainerStyle = css({
    display: "inline-flex"
});
/**
 * Props for the LanguageSelector component.
 */
export interface LanguageSelectorProps {
    selectedLanguage?: string;
    onLanguageChangeHandler?: React.Dispatch<string>;
    className?: string;
    testId?: string;
}

/**
 * Global footer's language selector component.
 * Uses React-Select to render the language selector with custom styling and on mouseover behavior.
 * Dropdown menu and the options have the same styling as WPL DropdownSelector.
 *
 * @param selectedLanguage - The selected language. Defaults to 'en'.
 * @param onChangeHandler - The function to call when a different language is selected.
 * @param className - The class name to apply to the container div.
 */
const LanguageSelector = ({
    selectedLanguage = "en",
    onLanguageChangeHandler,
    className,
    testId
}: LanguageSelectorProps) => {
    const intl = useIntl();
    const [isMouseOver, setMouseOver] = useState(false);
    const elementID = "language-dropdown";

    if (!onLanguageChangeHandler) {
        onLanguageChangeHandler = (newLocale) => {
            // check if there is already a locale in the path
            const { path } = parseRoute(window.location);

            // exclude trailing slash in the case of the home page
            window.location.href = path.length
                ? `/${newLocale}/${path}`
                : `/${newLocale}`;
        };
    }

    // TODO: Replace hardcoded color values with wacToken and ADS elevation tokens
    /* eslint-disable local/ensure-wac-token-usage */
    const languageSelectStyle: StylesConfig<LanguageType> = {
        container: (provided) => ({
            ...provided,
            appearance: "none",
            flexDirection: "row",
            fontSize: "0.875rem;",
            textAlign: "left"
        }),
        control: (provided, state) => ({
            ...provided,
            color: isMouseOver ? "#172b4d" : "#42526e",
            cursor: isMouseOver ? "pointer" : "default",
            display: "inline-flex",
            border: state.isFocused
                ? `2px solid #4c9aff`
                : "2px solid transparent"
        }),
        valueContainer: (provided) => ({
            ...provided,
            marginLeft: token("space.050"),
            textDecorationLine: isMouseOver ? "underline" : "none"
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            marginLeft: token("space.050")
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            fontSize: "1.5rem",
            height: "40px"
        }),
        menu: (provided) => ({
            ...provided,
            marginTop: token("space.050"),
            boxShadow: "0px 3px 5px 0px #091E4233",
            background: "#ffffff",
            borderRadius: token("space.050"),
            width: "5.5rem"
        }),
        option: (provided, state) => ({
            ...provided,
            "cursor": "pointer",
            "padding": `${token("space.100")} ${token("space.100")}`,
            "backgroundColor": state.isSelected
                ? "#b2d4ff"
                : state.isFocused
                ? "#ebecf0"
                : "#fafbfc",
            "color": state.isSelected ? "#0065ff" : "#172b4d",
            "&:hover": {
                background: state.isSelected ? "#b2d4ff" : "#ebecf0"
            },
            "&:active": {
                background: "#dfe1e5"
            }
        })
    };

    return (
        <>
            <VisuallyHidden>
                <label htmlFor={elementID}>
                    {intl.formatMessage({
                        defaultMessage: "Language selector",
                        id: "globalFooter.bottom.language-selector.label"
                    })}
                </label>
            </VisuallyHidden>
            {/* Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details. */}
            {/* eslint-disable jsx-a11y/no-static-element-interactions, @atlassian/a11y/mouse-events-have-key-events */}
            <div
                className={className}
                data-testid={testId}
                css={languageSelectorContainerStyle}
                onMouseEnter={() => setMouseOver(true)}
                onMouseLeave={() => setMouseOver(false)}
            >
                {/* eslint-enable jsx-a11y/no-static-element-interactions, @atlassian/a11y/mouse-events-have-key-events */}
                <UFOLabel name="global-footer-v2-language-selector-component">
                    <Select<LanguageType>
                        inputId={elementID}
                        defaultValue={languages.find(
                            (language) => language.value === selectedLanguage
                        )}
                        options={languages}
                        onChange={(selectedOption) => {
                            selectedOption &&
                                selectedOption.value !== selectedLanguage &&
                                onLanguageChangeHandler?.(selectedOption.value);
                        }}
                        unstyled={true}
                        // @ts-ignore
                        isMouseOver={isMouseOver}
                        onMenuClose={() => setMouseOver(false)}
                        aria-label={intl.formatMessage({
                            defaultMessage: "Choose your language",
                            id: "globalFooter.bottom.language-selector"
                        })}
                        components={{
                            Control,
                            DropdownIndicator,
                            ...(checkFeatureFlagGate(
                                featureFlags.wacLanguageSelectorLangAttribute
                            ) && { Option })
                        }}
                        styles={languageSelectStyle}
                        menuPlacement="auto"
                    />
                </UFOLabel>
            </div>
        </>
    );
};

export default LanguageSelector;
